import React, { Suspense, lazy, memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { isLinkPayAuthReturning } from "../../common/LinkPayAuth/isLinkPayAuthReturning";
import { isLinkPayAuthAvailable } from "../../common/LinkPayAuth/LinkPayAuthConfig";
import LazyLoadBoundary from "../../components/LazyLoadBoundary";
import Condition from "../../components/Conditional/Condition";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { isPaymentMethodEnabled, PAYMENT_METHOD } from "../../contexts/ConfigContext/paymentMethods";
import { usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../router/path";
import { useBackReturnsToCart } from "./useBackReturnsToCart";
import { addDatadogActionWithContext } from "../../services/datadog";
import AdyenCheckoutForm from "./AdyenCheckoutForm";
import LinkPayV2CheckoutForm from "./LinkPayV2CheckoutForm";
import PaymentDetail from "./PaymentDetail/PaymentDetail";

// Everything v3 — Auth0, the payment SDK and its stylesheets — lives in this chunk. A
// venue on v2, and any customer who never reaches a v3 payment page, downloads none of it.
const LinkPayV3CheckoutForm = lazy(() => import("./LinkPayV3CheckoutForm"));

import "./PaymentPage.scss";
import "./PaymentPage_flint.scss";
import "./PaymentPage_kokoro.scss";
import "./PaymentPage_pdd.scss";
import "./PaymentPage_shabuDays.scss";
import "./PaymentPage_wang.scss";

// A returning sign-in restores the order through the payment context, whose subscribers
// hear about it one commit after the auth params leave the url. Leaving for the cart is
// therefore never a same-render reflex: the reference arriving cancels the trip.
const BOUNCE_TO_CART_GRACE_MS = 300;
const SIGNING_IN_DEADLINE_MS = 15000;

const PaymentPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { state: paymentReference } = usePaymentContext((state) => state.paymentReference);
	const { state: stallMode } = useConfigContext((state) => state.stallMode);
	// LinkPass is handing the customer back and the order it carried has not landed yet.
	// Never true under v2 — no auth params ever ride a v2 url.
	const isSigningIn = isLinkPayAuthReturning();

	// Wherever this page came from — the cart, a scanned link, LinkPass — back means the cart.
	useBackReturnsToCart();

	useEffect(() => {
		if (isSigningIn) {
			// The only code that strips the auth params lives in the lazy v3 chunk; if that
			// chunk never arrives, nothing else ends this state. Anything not settled by
			// this deadline is not going to settle - give the customer their cart back.
			const deadline = window.setTimeout(() => {
				if (isLinkPayAuthReturning()) {
					navigate(PATH.CART, { replace: true });
				}
			}, SIGNING_IN_DEADLINE_MS);

			return () => window.clearTimeout(deadline);
		}

		if (paymentReference) {
			// Track payment page visit with paymentReference
			addDatadogActionWithContext(
				"payment_page_loaded",
				{
					stallMode: stallMode,
					timestamp: new Date().toISOString(),
				},
				paymentReference,
				true
			);
		} else {
			const bounce = window.setTimeout(() => {
				// Replace: this payment entry has nothing to offer, and a push would grow
				// the history by one dead entry per bounce.
				navigate(PATH.CART, { replace: true });
			}, BOUNCE_TO_CART_GRACE_MS);

			return () => window.clearTimeout(bounce);
		}
	}, [isSigningIn, paymentReference, navigate, stallMode]);

	/**
	 * One rule for every stall: the venue's payment_methods list (FOOD-3321) says what is
	 * on the page, and nothing else does. LinkPay comes in the version the venue's
	 * linkpay_v3_enabled picks - v3 is the LinkPass form (a guest sees the sign-in offer
	 * with the card form beside it when adyen is on, a signed-in member pays through the
	 * SDK alone); absent or false is the v2 injected-cookie flow, exactly as it shipped.
	 * A venue with LinkPay off offers the plain card form if adyen is on; a venue with
	 * neither runs view-only and never reaches this page.
	 */
	const { state: paymentMethods } = useConfigContext((state) => state.paymentMethods);
	const { state: linkpayV3Enabled } = useConfigContext(
		(state) => state.config.linkpay_v3_enabled
	);
	const linkPayOffered = isPaymentMethodEnabled(paymentMethods, PAYMENT_METHOD.LINKPAY);
	const adyenOffered = isPaymentMethodEnabled(paymentMethods, PAYMENT_METHOD.ADYEN);
	const isLinkPayV3 = linkpayV3Enabled ?? false;
	// A v3 slot on a build without its Auth0 config would render nothing at all - treat
	// it as not offered so the card form or the no-method message serves instead.
	const showLinkPayV3 = linkPayOffered && isLinkPayV3 && isLinkPayAuthAvailable;
	const showLinkPayV2 = linkPayOffered && !isLinkPayV3;
	const showAdyen = !showLinkPayV3 && !showLinkPayV2 && adyenOffered;
	const showNoMethod = !showLinkPayV3 && !showLinkPayV2 && !showAdyen;

	return (
		<div className="PaymentPage">
			<h2 className="PaymentPageTitle">{t("paymentPage.title")}</h2>
			<div className="PaymentPageContent">
				<PaymentDetail />
				<Condition check={showLinkPayV3}>
					<LazyLoadBoundary
						message={t("paymentPage.error.chunkLoadFailed")}
						retryLabel={t("paymentPage.linkpay.tryAgain")}
					>
						<Suspense fallback={null}>
							<LinkPayV3CheckoutForm />
						</Suspense>
					</LazyLoadBoundary>
				</Condition>
				<Condition check={showLinkPayV2}>
					<LinkPayV2CheckoutForm />
				</Condition>
				<Condition check={showAdyen}>
					<AdyenCheckoutForm />
				</Condition>
				<Condition check={showNoMethod}>
					<div className="PaymentError" data-testid="NoPaymentMethod">
						{t("paymentPage.error.noPaymentMethod")}
					</div>
				</Condition>
			</div>
		</div>
	);
};

export default memo(PaymentPage);
